/**
 * AW-2C-005 -- the provider projection to `LeadInspectorPanel`'s props.
 *
 * WHY AN ADAPTER
 * --------------
 * The route answers in the provider's own vocabulary (`first_name`,
 * `postal_code`, `vendor_lead_code`); the original panel was written
 * against three grouped objects (`customerInformation`, `address`,
 * `leadInformation`). The shapes are close enough that a spread would
 * appear to work and silently render nothing.
 *
 * ONLY NAMED KEYS CROSS
 * ---------------------
 * Every object below is built from named keys rather than spread from
 * the response, so a field the route starts returning tomorrow cannot
 * reach a panel without being added here on purpose. The output is a
 * subset of `WORKSPACE_TOOL_FIELD_ALLOWLISTS[LEAD]` in
 * `projectionContract.js`, and a test asserts that through the
 * contract's own validator.
 *
 * SEVEN FIELDS ARE DELIBERATELY ABSENT
 * ------------------------------------
 * `middleInitial`, `gender`, `dob`, `alternatePhone`, `address2`,
 * `address3` and `province` are left `undefined`, not `""`.
 *
 * The current Production intake never writes them (LEGACY-SPEC-001
 * S7/S12): DOB and gender go to the per-list `custom_<list_id>` table,
 * and the rest are simply not sent. `LeadField` renders nothing for an
 * empty value, so an absent field costs no row -- whereas `""` would
 * reserve a visible blank one that an agent reads as "this customer has
 * no middle initial" rather than "nothing was collected".
 *
 * DOB and gender both carry `emphasis="primary"` in the original
 * design. Their absence is a product consequence of the provider-backed
 * direction, recorded in AW-2C-002 S1, not an oversight here.
 *
 * Pure functions. No network, no state, no React.
 */

/** `null` for anything that is not a usable string. */
function text(value) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

/**
 * The route's response -> the panel's three sections.
 *
 * Returns `null` for a response that carries no lead identity: the
 * panel's own guard requires all three sections, and a half-built
 * payload would render blanks beside a real name.
 */
export function toLeadProjection(response) {
  const leadId = response?.lead_id;
  if (leadId === undefined || leadId === null) return null;

  return {
    customerInformation: {
      firstName: text(response.first_name),
      lastName: text(response.last_name),
      phone: text(response.phone_number),
      email: text(response.email),
      // middleInitial / gender / dob / alternatePhone: see module note.
    },
    address: {
      address1: text(response.address1),
      city: text(response.city),
      state: text(response.state),
      postalCode: text(response.postal_code),
      // address2 / address3 / province: see module note.
    },
    leadInformation: {
      vendorId: text(response.vendor_lead_code),
      // Native's own row id, rendered as the panel's "Lead ID" row and
      // already non-copyable there. A display value only: it is never
      // written to the Script Run, and never sent back to any endpoint.
      leadId: leadId === undefined ? undefined : String(leadId),
      comments: text(response.comments),
    },
  };
}

/**
 * The four merge-field values the Script runner may interpolate.
 *
 * Deliberately a SEPARATE, smaller projection than the panel's. A
 * Script is read aloud to the customer, so the vocabulary available to
 * it is a product decision rather than "whatever the panel has" --
 * `$(customer:email)` or `$(customer:vendor_id)` in an opening line
 * would be a mistake this shape makes impossible to write.
 */
export function toCustomerTokens(response) {
  if (!response) return null;
  return {
    first_name: text(response.first_name),
    last_name: text(response.last_name),
    city: text(response.city),
    state: text(response.state),
  };
}

/** The token ids `$(customer:…)` accepts. One place to change. */
export const CUSTOMER_TOKEN_IDS = Object.freeze([
  "first_name",
  "last_name",
  "city",
  "state",
]);
